import React, { useCallback, useMemo, useState } from "react";
import ProspectSearchHome from "./ProspectSearchHome";
import ProspectResults from "./ProspectResults";
import ProspectDetailsDrawer from "./ProspectDetailsDrawer";
import ProspectFiltersDrawer from "./ProspectFiltersDrawer";
import CampaignChoiceModal from "@/features/contacts/components/Apollo/CampaignChoiceModal";
import { useGetCampaigns } from "@/features/campaigns/hooks/useCampaign";
import { contactInfoWanted, useProspectSearch } from "../../hooks/useProspectSearch";
import { useProspectImport } from "../../hooks/useProspectImport";
import { countActiveFilters } from "../../utils/prospectUtils";
import type { ProspectItem, SavedSearchItem, SearchCampaign } from "../../types";

const CampaignChoiceModalComponent = CampaignChoiceModal as React.ComponentType<any>;

export default function ProspectsTab() {
  const prospectSearch = useProspectSearch();
  const { search, searchByCampaign, applyPanelFilters, startNewSearch: resetSearch } = prospectSearch;
  const contactWanted = contactInfoWanted(prospectSearch.appliedFilters);
  const { importedIds, importingIds, isBulkImporting, importOne, importMany } = useProspectImport(contactWanted);
  const [selection, setSelection] = useState<ProspectItem[]>([]);
  // People already imported can't be selected (or imported) again.
  const selected = useMemo(() => selection.filter((p) => !importedIds.has(p.id)), [selection, importedIds]);
  const setSelected = useCallback(
    (rows: ProspectItem[]) => setSelection(rows.filter((p) => !importedIds.has(p.id))),
    [importedIds],
  );
  const [detail, setDetail] = useState<ProspectItem | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  // Contacts waiting for the user to pick a campaign in the chooser.
  const [campaignTargets, setCampaignTargets] = useState<ProspectItem[] | null>(null);
  const { data: campaignsData } = (useGetCampaigns as any)(1, 100, "", campaignTargets !== null);

  // Every new search starts with a clean selection and a closed drawer.
  const resetView = useCallback(() => {
    setSelection([]);
    setDetail(null);
  }, []);

  const runSearch = useCallback(
    (query: string) => {
      resetView();
      search(query);
    },
    [resetView, search],
  );

  const runCampaignSearch = useCallback(
    (campaign: SearchCampaign) => {
      resetView();
      searchByCampaign(campaign);
    },
    [resetView, searchByCampaign],
  );

  const runSavedSearch = useCallback(
    (saved: SavedSearchItem) => {
      if (saved.campaignId) {
        runCampaignSearch({ id: saved.campaignId, name: saved.campaignName || "Campaign" });
      } else {
        runSearch(saved.query);
      }
    },
    [runCampaignSearch, runSearch],
  );

  const runFilterSearch = useCallback(() => {
    resetView();
    setShowFilters(false);
    applyPanelFilters();
  }, [resetView, applyPanelFilters]);

  const startNewSearch = useCallback(() => {
    resetView();
    resetSearch();
  }, [resetView, resetSearch]);

  // Clicking the open contact again closes the drawer.
  const toggleDetail = useCallback((prospect: ProspectItem) => {
    setDetail((current) => (current?.id === prospect.id ? null : prospect));
  }, []);

  const handleBulkImport = useCallback(async () => {
    if (await importMany(selected)) setSelection([]);
  }, [importMany, selected]);

  // Results from a campaign's ICP go straight to that campaign; otherwise ask which one.
  const handleAddToCampaign = useCallback(async () => {
    if (!prospectSearch.campaign) {
      setCampaignTargets(selected);
      return;
    }
    if (await importMany(selected, [prospectSearch.campaign.id])) setSelection([]);
  }, [importMany, selected, prospectSearch.campaign]);

  const handleCampaignChosen = useCallback(
    async (campaignId: string | number) => {
      const targets = campaignTargets || [];
      setCampaignTargets(null);
      if (targets.length === 1) {
        await importOne(targets[0], [campaignId]);
        return;
      }
      if (await importMany(targets, [campaignId])) setSelection([]);
    },
    [campaignTargets, importOne, importMany],
  );

  // The saved search behind the results on screen, highlighted in the saved-search picker.
  const selectedSavedId = useMemo(() => {
    const { campaign, lastQuery } = prospectSearch;
    const query = (lastQuery || "").trim().toLowerCase();
    const match = prospectSearch.savedSearches.find((s) =>
      campaign ? String(s.campaignId) === String(campaign.id) : !s.campaignId && s.query.trim().toLowerCase() === query,
    );
    return match ? match.id : null;
  }, [prospectSearch]);

  const cellContext = useMemo(
    () => ({
      importedIds,
      importingIds,
      onOpen: toggleDetail,
      onImport: (prospect: ProspectItem) => importOne(prospect),
      onImportToCampaign: (prospect: ProspectItem) => setCampaignTargets([prospect]),
      wantPhone: contactWanted.phone,
    }),
    [importedIds, importingIds, toggleDetail, importOne, contactWanted.phone],
  );

  return (
    <>
      {prospectSearch.showResults ? (
        <ProspectResults
          queryName={prospectSearch.activeQueryName}
          queryText={prospectSearch.queryText}
          onQueryChange={prospectSearch.setQueryText}
          onSearch={runSearch}
          onNewSearch={startNewSearch}
          savedSearches={prospectSearch.savedSearches}
          suggestions={prospectSearch.suggestions}
          selectedSavedId={selectedSavedId}
          onSelectSaved={runSavedSearch}
          prospects={prospectSearch.prospects}
          isSearching={prospectSearch.isSearching}
          hasMore={prospectSearch.hasMore}
          isLoadingMore={prospectSearch.isLoadingMore}
          onFindMore={prospectSearch.findMore}
          selected={selected}
          onSelectedChange={setSelected}
          isBulkImporting={isBulkImporting}
          onBulkImport={handleBulkImport}
          campaign={prospectSearch.campaign}
          onAddToCampaign={handleAddToCampaign}
          onOpenFilters={() => setShowFilters(true)}
          activeFilterCount={countActiveFilters(prospectSearch.panelFilters, prospectSearch.appliedFilters)}
          onRetry={prospectSearch.retry}
          cellContext={cellContext}
        />
      ) : (
        <ProspectSearchHome
          value={prospectSearch.queryText}
          onChange={prospectSearch.setQueryText}
          onSearch={runSearch}
          isLoading={prospectSearch.isSearching}
          savedSearches={prospectSearch.savedSearches}
          onDeleteSavedSearch={prospectSearch.deleteSavedSearch}
          onCampaignSearch={runCampaignSearch}
          onRunSaved={runSavedSearch}
        />
      )}

      <ProspectFiltersDrawer
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        filters={prospectSearch.panelFilters}
        setFilters={prospectSearch.setPanelFilters}
        appliedFilters={prospectSearch.appliedFilters}
        onApply={runFilterSearch}
        onClear={prospectSearch.clearPanelFilters}
        isApplying={prospectSearch.isSearching}
      />

      <CampaignChoiceModalComponent
        isOpen={campaignTargets !== null}
        campaigns={campaignsData?.campaigns ?? []}
        onSelectCampaign={handleCampaignChosen}
        onClose={() => setCampaignTargets(null)}
        isBusy={isBulkImporting}
      />

      <ProspectDetailsDrawer
        prospect={detail}
        isImported={detail ? importedIds.has(detail.id) : false}
        isImporting={detail ? importingIds.has(detail.id) : false}
        wantPhone={contactWanted.phone}
        onImport={async (prospect) => {
          if (await importOne(prospect)) setDetail(null);
        }}
        onClose={() => setDetail(null)}
      />
    </>
  );
}
